// profile.effects.ts

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { exhaustMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { ApiService } from '../../../../services';
import * as ProfileActions from './profile.actions';
import { LoginMethodResult } from './profile.actions';
import { UpdateUserTwoFaPayload } from 'src/app/models';
import { LoginMethodStatusEnum } from 'src/app/components/profile/security-settings/login-methods/login-methods.component';

/**
 * Profile Effects
 * Handles all profile-related side effects
 */
@Injectable()
export class ProfileEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private router: Router,
    private store: Store
  ) {}

  /**
   * Update user profile (name, bio, etc.)
   */
  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.userProfileActions.update),
      exhaustMap(({ payload }) =>
        this.api
          .updateProfile(payload, (error: any) => {
            throw error;
          })
          .pipe(
            map((response) =>
              ProfileActions.userProfileActions.updateSuccess({
                learmoProfile: response,
              })
            ),
            catchError((error: any) =>
              of(ProfileActions.userProfileActions.updateFailure({ error }))
            )
          )
      )
    )
  );

  /**
   * Update user email with password verification
   */
  updateUserEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.userEmailActions.update),
      exhaustMap(({ payload }) =>
        this.api
          .changeEmailRequest(payload, (error: any) => {
            throw error;
          })
          .pipe(
            map((response) =>
              ProfileActions.userEmailActions.updateSuccess({ response })
            ),
            catchError((error: any) => {
              console.error('Email update error:', error);
              return of(
                ProfileActions.userEmailActions.updateFailure({ error })
              );
            })
          )
      )
    )
  );

  /**
   * Enable two-factor authentication
   */
  enable2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.enable2FAActions.enable),
      exhaustMap(() => {
        const payload: UpdateUserTwoFaPayload = { twofa: 1 };

        return this.api
          .updateUserSettings(payload, (error: any) => {
            throw error;
          })
          .pipe(
            map(() => ProfileActions.enable2FAActions.enableSuccess()),
            catchError((error: any) => {
              console.error('2FA enable error:', error);
              return of(
                ProfileActions.enable2FAActions.enableFailure({ error })
              );
            })
          );
      })
    )
  );

  /**
   * Disable two-factor authentication
   */
  disable2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.enable2FAActions.disable),
      exhaustMap(() => {
        const payload: UpdateUserTwoFaPayload = { twofa: 0 };

        return this.api
          .updateUserSettings(payload, (error: any) => {
            throw error;
          })
          .pipe(
            map(() => ProfileActions.enable2FAActions.disableSuccess()),
            catchError((error: any) => {
              console.error('2FA disable error:', error);
              return of(
                ProfileActions.enable2FAActions.disableFailure({ error })
              );
            })
          );
      })
    )
  );

  /**
   * Get user's available login methods and their status
   */
  getUserLoginMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.userLoginMethodConfigActions.get),
      exhaustMap(() =>
        this.api
          .getLoginMethodsStatus((error: any) => {
            throw error;
          })
          .pipe(
            map((response: LoginMethodResult[]) =>
              ProfileActions.userLoginMethodConfigActions.getSuccess({
                loginMethodsResults: response,
              })
            ),
            catchError((error: any) => {
              console.error('Get login methods error:', error);
              return of(
                ProfileActions.userLoginMethodConfigActions.getFailure({
                  error,
                })
              );
            })
          )
      )
    )
  );

  /**
   * Update login method status (enable/disable)
   */
  updateUserLoginMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.changeLoginMethodConfigActions.change),
      exhaustMap(({ method, enabled }) => {
        const methodStatus = enabled
          ? LoginMethodStatusEnum.enabled
          : LoginMethodStatusEnum.disabled;

        return this.api
          .updateLoginMethodsStatus(method, enabled, (error: any) => {
            throw error;
          })
          .pipe(
            map(() =>
              ProfileActions.changeLoginMethodConfigActions.changeSuccess({
                method,
                enabled: methodStatus,
              })
            ),
            catchError((error: any) => {
              console.error('Update login method error:', error);
              return of(
                ProfileActions.changeLoginMethodConfigActions.changeFailure({
                  error,
                })
              );
            })
          );
      })
    )
  );

  /**
   * Show success message after profile update
   */
  profileUpdateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileActions.userProfileActions.updateSuccess),
        tap(() => {
          // You can add toast notification here
          console.log('Profile updated successfully');
        })
      ),
    { dispatch: false }
  );

  /**
   * Show success message after email update
   */
  emailUpdateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileActions.userEmailActions.updateSuccess),
        tap(() => {
          // You can add toast notification here
          console.log('Email updated successfully');
        })
      ),
    { dispatch: false }
  );

  /**
   * Show success message after 2FA is enabled
   */
  enable2FASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileActions.enable2FAActions.enableSuccess),
        tap(() => {
          // You can add toast notification here
          console.log('Two-factor authentication enabled');
        })
      ),
    { dispatch: false }
  );
}